import { useEffect, useState } from "react";
import { ACCOUNT_GATE_EVENT, api, SITE_HOST } from "./api";
import { AgreementGate } from "./pages/AgreementGate";
import { Suspended } from "./pages/Suspended";
import { Landing } from "./pages/Landing";
import { Verify } from "./pages/Verify";
import { Reset } from "./pages/Reset";
import { Dashboard } from "./pages/Dashboard";
import { Logo } from "./components/Logo";
import { ThemeToggle } from "./components/ThemeToggle";

type AuthState =
  | { kind: "loading" }
  | { kind: "anon" }
  | { kind: "suspended"; email: string; id: string; since: number }
  | { kind: "agreement"; email: string; id: string; version: string }
  | { kind: "customer"; email: string; id: string };

function tokenFromUrl(path: string): string | null {
  if (typeof window === "undefined") return null;
  if (window.location.pathname !== path) return null;
  const token = new URLSearchParams(window.location.search).get("token");
  return token && token.length > 0 ? token : null;
}

// The public marketing site (otp.mioe.com.tr) deep-links into the embedded
// form via ?register / ?login (or the matching hash); pick which tab opens.
// SMS login is the default when there's no hint.
function initialAuthMode(): "sms" | "login" | "signup" {
  if (typeof window === "undefined") return "sms";
  const hint = (window.location.search + window.location.hash).toLowerCase();
  if (/(register|signup|kayit|kayıt)/.test(hint)) return "signup";
  if (/login|giris|giriş|parola|password|sifre|şifre/.test(hint)) return "login";
  return "sms";
}

export function App() {
  const [auth, setAuth] = useState<AuthState>({ kind: "loading" });
  const verifyToken = tokenFromUrl("/verify");
  const resetToken = tokenFromUrl("/reset");

  // Every way into the dashboard goes through here, so a suspended account
  // lands on the notice and one that has not accepted the current user
  // agreement lands on the gate, before anything else.
  function loadMe() {
    api
      .me()
      .then((me) => {
        if (me.suspended_at) {
          setAuth({ kind: "suspended", email: me.email, id: me.id, since: me.suspended_at });
        } else if (!me.agreement.accepted) {
          setAuth({ kind: "agreement", email: me.email, id: me.id, version: me.agreement.version });
        } else {
          setAuth({ kind: "customer", email: me.email, id: me.id });
        }
      })
      .catch(() => setAuth({ kind: "anon" }));
  }

  useEffect(() => {
    if (verifyToken || resetToken) {
      setAuth({ kind: "anon" });
      return;
    }
    loadMe();
  }, [verifyToken, resetToken]);

  useEffect(() => {
    window.addEventListener(ACCOUNT_GATE_EVENT, loadMe);
    return () => window.removeEventListener(ACCOUNT_GATE_EVENT, loadMe);
  }, []);

  let content;
  if (resetToken) {
    content = <Reset token={resetToken} />;
  } else if (verifyToken) {
    content = (
      <Verify
        token={verifyToken}
        onVerified={() => {
          window.history.replaceState(null, "", "/");
          loadMe();
        }}
      />
    );
  } else if (auth.kind === "loading") {
    content = (
      <div className="login-shell">
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
          <Logo size={48} title={SITE_HOST} />
          <span className="muted">Loading…</span>
        </div>
      </div>
    );
  } else if (auth.kind === "anon") {
    content = (
      <Landing
        initialMode={initialAuthMode()}
        onLogin={() => loadMe()}
      />
    );
  } else if (auth.kind === "suspended") {
    content = (
      <Suspended
        email={auth.email}
        since={auth.since}
        onLogout={() => setAuth({ kind: "anon" })}
      />
    );
  } else if (auth.kind === "agreement") {
    content = (
      <AgreementGate
        email={auth.email}
        version={auth.version}
        onAccepted={loadMe}
        onLogout={() => setAuth({ kind: "anon" })}
      />
    );
  } else {
    content = (
      <Dashboard
        meEmail={auth.email}
        onLogout={() => setAuth({ kind: "anon" })}
      />
    );
  }

  return (
    <>
      {content}
      <ThemeToggle />
    </>
  );
}
